import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../translations/en';
import fr from '../translations/fr';
import es from '../translations/es';
import tr from '../translations/tr';

// All translations
const translations = {
  en,
  fr,
  es,
  tr
};

/**
 * Custom hook to get translations based on the current language
 * @returns {Object} An object with the t function to get translations
 */
export const useTranslation = () => {
  const { currentLanguage } = useLanguage();

  /**
   * Get a translation by key
   * @param {string} key - The translation key (dot notation supported, e.g., 'menu.title')
   * @param {Object} params - Optional parameters to replace placeholders in the translation
   * @returns {string} The translated text
   */
  const t = React.useCallback((key, params = {}) => {
    // Get the translation for the current language
    const translationObj = translations[currentLanguage] || translations.en;

    // Split the key by dots to access nested properties
    const keys = key.split('.');

    // Navigate through the translation object to find the requested translation
    let translation = translationObj;
    for (const k of keys) {
      translation = translation?.[k];

      // If translation is not found, return the key
      if (translation === undefined) {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }

    // If the translation is not a string, return it as is (useful for arrays, objects, etc.)
    if (typeof translation !== 'string') {
      return translation;
    }

    // Replace placeholders with provided parameters
    let result = translation;
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
    });

    return result;
  }, [currentLanguage]);

  return { t };
};

export default translations;
